import { useEffect, useMemo } from 'react';

import { FieldTypes } from 'constants/field-types-enum';
import { MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import {
  createLogicJumpsMessage,
  getAffectedLogicJumps,
  getLogicJumpsForField,
} from 'helpers/form';
import { useDispatch } from 'react-redux';

import { messagePopUp } from 'api/app-slice';

export const DEFAULT_EMPTY_FIELD = {
  text: '',
  check_part: 'CUSTOM',
  type: FieldTypes.FreeText,
  required: true,
  options: { required: true, options: [{ value: '' }] },
};

export const useQuestionnaireBuilder = ({
  fields,
  logicJumps,
  formRules,
  append,
  move,
  removeLogic,
  setValue,
  blockBeforeFields,
  setCopyFieldDetails,
  copyFieldDetails,
  setLogicJumpAlertMessage,
  setDragInfo,
  setFieldReorderAlert,
  setDeleteFieldLogicJumps,
  setDeleteFieldAlert,
  setDeleteFieldMessage,
  setDeleteFieldIndex,
}) => {
  const dispatch = useDispatch();

  const addField = () => {
    append({ ...DEFAULT_EMPTY_FIELD, order: (fields?.length ?? 0) + 1 });
  };

  const removeLogicJumps = useMemo(() => {
    return logicJumpsAttached => {
      const indicesToRemove = (logicJumpsAttached ?? [])
        .map(jump =>
          (logicJumps ?? []).findIndex(
            lj =>
              (lj.id !== undefined && lj.id === jump.id) ||
              (lj.from === jump.from && lj.to === jump.to && lj.field === jump.field),
          ),
        )
        .filter(index => index !== -1)
        .sort((a, b) => b - a);
      (indicesToRemove ?? []).forEach(index => removeLogic(index));
    };
  }, [logicJumps, removeLogic]);

  const reorderFields = (source, destination, logicJumpsAttached = []) => {
    removeLogicJumps(logicJumpsAttached);

    if (!source || !destination || destination.index < blockBeforeFields) return;
    const srcIndex = source?.index ?? 0;
    const dstIndex = destination?.index ?? 0;
    let first, last;

    if (srcIndex > dstIndex) {
      first = dstIndex;
      last = srcIndex;
    } else {
      first = srcIndex;
      last = dstIndex;
    }

    move(srcIndex, dstIndex);
    for (let i = first; i <= last; i++) setValue(`fields[${i}].order`, i + 1);
  };

  const onDragEnd = ({ source, destination }) => {
    if (!source || !destination) return;
    const currentOrder = (source?.index ?? 0) + 1;
    const targetOrder = (destination?.index ?? 0) + 1;

    const logicJumpsAttached = getAffectedLogicJumps(logicJumps ?? [], currentOrder, targetOrder);
    const message = createLogicJumpsMessage(logicJumpsAttached ?? []);

    if ((logicJumpsAttached?.length ?? 0) > 0) {
      setLogicJumpAlertMessage(message);
      setDragInfo({
        source,
        destination,
        logicJumpsAttached,
      });
      setFieldReorderAlert(true);
      return;
    } else {
      reorderFields(source, destination);
    }
  };

  const handleDeleteField = index => {
    const field = fields?.[index];
    if (field == null) return;
    const logicJumpsAttached = getLogicJumpsForField(logicJumps ?? [], field.order);
    const message =
      logicJumpsAttached?.length > 0 ? createLogicJumpsMessage(logicJumpsAttached) : '';
    setDeleteFieldLogicJumps(logicJumpsAttached);
    setDeleteFieldAlert(true);
    setDeleteFieldMessage(message);
    setDeleteFieldIndex(index);
  };

  const duplicateField = field => {
    if (field == null) return;
    const { id, ...fieldWithoutId } = field;
    const newField = { ...fieldWithoutId, order: (fields?.length ?? 0) + 1 };
    append(newField);
    dispatch(
      messagePopUp({
        text: 'Field duplicated successfully - Your field has been added to the bottom so the form logic remains intact.',
        state: MESSAGE_STATE_SUCCESS,
        hide: true,
      }),
    );
  };

  const copyField = field => {
    if (field == null) return;
    const { id, ...fieldWithoutId } = field;
    const newField = { ...fieldWithoutId };
    setCopyFieldDetails(newField);
  };

  const pasteField = (field, targetIndex) => {
    if (field == null) return;
    append(field);
    move(fields?.length ?? 0, targetIndex);
    setCopyFieldDetails(null);
  };

  const pasteFieldWithLogicCheck = (targetOrder, targetIndex) => {
    if (copyFieldDetails == null) return;
    const logicJumpsAttached = getLogicJumpsForField(logicJumps ?? [], targetOrder);
    if ((logicJumpsAttached?.length ?? 0) > 0) {
      // Handle logic jumps alert if needed
      return;
    }

    const newField = { ...copyFieldDetails, order: targetOrder };
    pasteField(newField, targetIndex);
  };

  const pasteFieldAbove = field => {
    if (field == null || field.order == null) return;
    pasteFieldWithLogicCheck(field.order, field.order - 1);
  };

  const pasteFieldBelow = field => {
    if (field == null || field.order == null) return;
    pasteFieldWithLogicCheck(field.order + 1, field.order);
  };

  const getLogicJumpsCount = order => {
    if (order == null) return 0;
    return (
      (logicJumps ?? []).filter(jump => jump?.from === order || jump?.to === order)?.length ?? 0
    );
  };

  const getRulesCount = order => {
    if (order == null) return 0;
    const matchingRules = (formRules ?? []).filter(
      rule =>
        rule != null && rule?.field === order && rule?.active && rule?.rule_type !== 'logic_jump',
    );

    const count = [
      matchingRules.some(r => r?.rule_type === 'block'),
      matchingRules.some(r => r?.rule_type === 'flag'),
    ].filter(Boolean).length;

    return count;
  };

  return {
    addField,
    reorderFields,
    removeLogicJumps,
    onDragEnd,
    handleDeleteField,
    duplicateField,
    copyField,
    pasteField,
    pasteFieldWithLogicCheck,
    pasteFieldAbove,
    pasteFieldBelow,
    getLogicJumpsCount,
    getRulesCount,
  };
};

/**
 * Form-level rules count: per field at most 1 for block and 1 for flag (same logic as getRulesCount).
 */
export const getFormRulesCount = formRules => {
  if (formRules == null || !Array.isArray(formRules)) return 0;
  const activeRules = formRules.filter(
    r => r != null && r?.active && r?.rule_type !== 'logic_jump',
  );
  const orders = [...new Set(activeRules.map(r => r?.field).filter(o => o != null))];
  return orders.reduce((sum, order) => {
    const matchingRules = activeRules.filter(r => r != null && r?.field === order);
    return (
      sum +
      [
        matchingRules.some(r => r?.rule_type === 'block'),
        matchingRules.some(r => r?.rule_type === 'flag'),
      ].filter(Boolean).length
    );
  }, 0);
};
